
import React, { useEffect, useState } from "react";
import { SEO } from "@/components/seo/SEO";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import MainLayout from "@/components/layout/MainLayout";
import { DataImport } from "@/components/data/DataImport";
import { DataPreview } from "@/components/data/DataPreview";
import { DataSources } from "@/components/data/DataSources";
import { WebhookConfig } from "@/components/data/WebhookConfig";
import { ScheduledTasks } from "@/components/automation/ScheduledTasks";
import { AIDataCleaner } from "@/components/ai/AIDataCleaner";
import { DashboardSuggestionsAI } from "@/components/ai/DashboardSuggestionsAI";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import ErrorLogger from "@/utils/errorLogger";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HubSpotOAuthIntegration } from "@/components/data/integrations/HubSpotOAuthIntegration";
import { ZohoOAuthIntegration } from "@/components/data/integrations/ZohoOAuthIntegration";
import { DropboxSheetsIntegration } from "@/components/data/integrations/DropboxSheetsIntegration";
import { OneDriveExcelIntegration } from "@/components/data/integrations/OneDriveExcelIntegration";
import { GoogleSheetsOAuthIntegration } from "@/components/data/integrations/GoogleSheetsOAuthIntegration";
import { ConnectionStatusDashboard } from "@/components/data/ConnectionStatusDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { DEV_MODE } from "@/lib/dev-mode";

const Data = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Use React Query for session management for better error handling and caching
  const { data: sessionData, isLoading } = useQuery({
    queryKey: ["auth-session"],
    queryFn: async () => {
      try {
        console.log("Checking authentication on Data Collection page");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Authentication session check error:", error.message);
          // Don't throw error on initial page load, just return null session
          return { 
            session: null, 
            isAuthenticated: false 
          };
        }
        
        return { 
          session: data.session, 
          isAuthenticated: !!data.session 
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
        console.error("Error checking authentication:", errorMessage);
        // Don't log errors during initial load to avoid error popups
        return { 
          session: null, 
          isAuthenticated: false 
        };
      }
    },
    retry: false, // Don't retry to avoid multiple error messages
    refetchOnWindowFocus: false, // Prevent refetching when window regains focus
  });

  // Handle OAuth callback params and refresh CRM list
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        const oauthSuccess = searchParams.get("oauth_success");
        const oauthError = searchParams.get("oauth_error");
        const crmType = searchParams.get("crm_type");
  
        if (oauthSuccess === "true" && crmType) {
          const formattedCrmType = crmType.charAt(0).toUpperCase() + crmType.slice(1).replace('_', ' ');
          toast.success(`✅ ${formattedCrmType} connected successfully!`);
          
          // Refresh the CRM list to show updated status
          queryClient.invalidateQueries({ queryKey: ['crm-integrations'] });
          
          // Clear the URL parameters to prevent showing toast again on refresh
          window.history.replaceState({}, '', '/data');
        } else if (oauthError) {
          toast.error(`Failed to connect: ${oauthError}`);
          // Clear error from URL
          window.history.replaceState({}, '', '/data');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Error processing OAuth response";
        console.error("OAuth callback error:", errorMessage);
      }
    }, 1000); // Delay toast notifications
    
    return () => clearTimeout(timeoutId);
  }, [searchParams, queryClient]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Checking authentication...</span>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">
            Please refresh the page or try signing in again.
          </p>
        </div>
      </MainLayout>
    );
  }

  if (!DEV_MODE && !sessionData?.isAuthenticated) {
    return (
      <MainLayout>
        <Alert variant="warning" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            You must be logged in to view this page.
          </AlertDescription>
        </Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO title="Data Collection — Veylo AI" description="Import, preview and schedule business data from CSV, Excel, Google Sheets, HubSpot, Zoho and custom webhooks in Veylo AI." path="/data" />
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Data Collection & Cleaning</h1>
        <p className="text-muted-foreground mb-6">
          Connect to your CRM systems, import data from various sources, clean it with AI, and set up automated data collection with AI-powered insights.
        </p>
        <div className="grid gap-6">
          <ConnectionStatusDashboard />
          <AIDataCleaner />
          <DataImport />
          
          {/* CRM Integrations with OAuth */}
          <div className="grid md:grid-cols-2 gap-6">
            <HubSpotOAuthIntegration />
            <ZohoOAuthIntegration />
          </div>
          
          {/* Cloud Storage + Spreadsheet Integrations */}
          <div className="grid md:grid-cols-2 gap-6">
            <DropboxSheetsIntegration />
            <OneDriveExcelIntegration />
          </div>
          
          {/* Standalone Spreadsheet Integration */}
          <div className="grid md:grid-cols-1 gap-6">
            <GoogleSheetsOAuthIntegration />
          </div>
          
          {/* Webhook Configuration */}
          <WebhookConfig />
          
          <DashboardSuggestionsAI />
          <DataSources />
          <DataPreview />
          <ScheduledTasks />

          <Card>
            <CardHeader>
              <CardTitle>Workflow setup lives in AI Workflows</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Your industry, cleaning rules, prediction goals and dashboard preferences are set
                up in one place, then reused by every run.
              </p>
              <Button asChild>
                <Link to="/ai-workflows">
                  Open AI Workflows
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default Data;
